<template>
  <div class="content">
    <h1 class="has-text-centered">HTTP Protokoll</h1>
    <div class="box">
      <p>Eine kompakte Zusammenfassung über das HTTP Protokoll.</p>
      <FAQsTableOfContents :faqs="faqs" />
    </div>

    <div class="box">
      <FAQs :faqs="faqs" :h_level="2" />
    </div>

    <FeedbackBox />
  </div>
</template>

<script>
import FeedbackBox from '@/components/FeedbackBox.vue'
import FAQs from '@/components/FAQs.vue'
import FAQsTableOfContents from '@/components/FAQsTableOfContents.vue'

export default {
  name: 'theory-http-protocol',
  components: {
    FeedbackBox,
    FAQs,
    FAQsTableOfContents
  },
  metaInfo: {
    title: 'HTTP Protokoll | Elektrotechnik-Rechner.at',
    meta: [
      {
        name: 'description',
        content: 'Eine kompakte Zusammenfassung über das HTTP Protokoll.'
      }
    ]
  },
  created () {
    this.$store.commit('updateNavTitle', { title: '<span class="fas fa-angle-left"></span> Zurück', route: 'network-engineering' })
  },
  data () {
    return {
      faqs: [
        {
          question: 'Was ist HTTP?',
          answer:
            '<ul>' +
            '  <li>HTTP = <strong>H</strong>yper<strong>t</strong>ext <strong>T</strong>ransfer <strong>P</strong>rotocol</li>' +
            '  <li>Zustandsloses Protokoll für die Übertragung von Daten über ein Rechnernetz</li>' +
            '  <li>Ein Standard welcher im RFC festgelegt ist</li>' +
            '  <li>Dient im Internet zur Auslieferung von Webseiten</li>' +
            '  <li>Gehört zur Internetprotokollfamilie</li>' +
            '  <li>Schicht 7 (Anwendungsschicht) des OSI-Modells</li>' +
            '  <li>Benutzt Port 80</li>' +
            '</ul>'
        },
        {
          question: 'Was ist ein RFC?',
          answer:
            '<ul>' +
            '  <li>RFC = <strong>R</strong>equest <strong>f</strong>or <strong>C</strong>omments</li>' +
            '  <li>Dokumente die von der IETF (Internet Engineering Task Force) herausgegeben werden</li>' +
            '  <li>Behandeln Protokolle, Methode, Programme und Konzepte für die Zusammenarbeit unterschiedlicher Systeme im Internet</li>' +
            '</ul>'
        },
        {
          question: 'Was ist ein zustandsloses Protokoll?',
          answer:
            '<ul>' +
            '  <li>Mit jeder Anforderung wird die Kommunikation von vorne begonnen</li>' +
            '  <li>Informationen aus früheren Anforderungen gehen verloren</li>' +
            '</ul>' +
            '<h3>Wie ist HTTP aufgebaut?</h3>' +
            '<ul>' +
            '  <li>Eine HTTP Kommunikation besteht aus zwei Arten von Nachrichten</li>' +
            '  <ul>' +
            '    <li><strong>Request:</strong> Anfrage von Client an den Server</li>' +
            '    <ul>' +
            '      <li>' +
            '        <u>Beispiel: </u><br />' +
            '        <i>' +
            '          GET /infotext.html HTTP/1.1<br />' +
            '          Host: www.example.net' +
            '        </i>' +
            '      </li>' +
            '    </ul>' +
            '    <li><strong>Response:</strong> Antwort von Server an den Client</li>' +
            '    <ul>' +
            '      <li>' +
            '        <u>Beispiel:</u><br />' +
            '        <i>' +
            '          HTTP/1.1 200 OK<br />' +
            '          Server: Apache/1.3.29 (Unix) PHP/4.3.4<br />' +
            '          Content-Length: 123456<br />' +
            '          Content-Language: de<br />' +
            '          Connection: close<br />' +
            '          Content-Type: text/html<br />' +
            '          (Inhalt von infotext.html)' +
            '        </i>' +
            '      </li>' +
            '    </ul>' +
            '  </ul>' +
            '  <li>Jede Nachricht besteht aus zwei Teilen:</li>' +
            '  <ul>' +
            '    <li><strong>Nachrichtenkopf ("HTTP Header")</strong></li>' +
            '    <ul>' +
            '      <li>Enthält neben den Informationen über den Nachrichtenrumpf, auch Informationen über den Status des Senders, akzeptierte Inhalte, Bitte nach einem Protokollwechsel</li>' +
            '      <li>Gibt an wie die Daten im Nachrichtenrumpf (falls vorhanden) zu interpretieren sind</li>' +
            '    </ul>' +
            '    <li><strong>Nachrichtenrumpf ("HTTP Message Body")</strong></li>' +
            '    <ul>' +
            '      <li>Üblicherweise die vom Client/Browser angeforderte Ressource. (Sofern sie zur Verfügung stand)</li>' +
            '    </ul>' +
            '  </ul>' +
            '  <li>Es gibt mehrere HTTP Anfrage Methoden. Hier die beiden meistgenutzten</li>' +
            '  <ul>' +
            '    <li><strong>GET:</strong> Fordert eine bestimmte Ressource (Website, Datei, ...) an</li>' +
            '    <li><strong>POST:</strong> Schickt Daten an den Server</li>' +
            '  </ul>' +
            '</ul>'
        },
        {
          question: 'Was ist HTTPS?',
          answer:
            '<ul>' +
            '  <li>HTTPS = <strong>H</strong>yper<strong>t</strong>ext <strong>T</strong>ransfer <strong>P</strong>rotocol <strong>S</strong>ecure</li>' +
            '  <li>Verschlüsselte Datenübertragung</li>' +
            '  <li>Verschlüsselung mit TLS (<strong>T</strong>ransport <strong>L</strong>ayer <strong>S</strong>ecurity) - früher SSL (<strong>S</strong>ecure <strong>S</strong>ockets <strong>L</strong>ayer)</li>' +
            '  <li>Nutzt Port 443</li>' +
            '</ul>' +
            '<h3>Ablauf</h3>' +
            '<ol>' +
            '  <li>Identifikation und Authentifizierung der Kommunikationspartner nach <strong>SSL-Handshake-Protokoll</strong></li>' +
            '  <li>Austausch des symmetrischen Sitzungsschlüssels mittels <strong>asymmetrischer Verschlüsselung</strong></li>' +
            '  <li>Der Schlüssel wird ausschließlich zur Verschlüsselung der Nutzdaten verwendet</li>' +
            '</ol>' +
            '<h4>SSL-Handshake</h4>' +
            '<ol>' +
            '  <li>Verbindungsaufbau - Client und Server schicken sich gegenseitig "Hello"-Pakete und eine Zufallszahl, welche später zum errechnen des Session Keys genutzt wird</li>' +
            '  <li>Server authentifiziert sich beim Client mittels Zertifikat (optional)</li>' +
            '  <li>Client authentifiziert sich beim Server mittels Zertifikat (optional)</li>' +
            '  <li>Session Key wird erstellt und die authentifizierten Partner können verschlüsselt Daten übertragen</li>' +
            '</ol>' +
            '<h4>Asymmetrische Verschlüsselung</h4>' +
            '<p>Es werden zwei Schlüssel eingesetzt: öffentlicher und privater Schlüssel.</p>' +
            '<ol>' +
            '  <li>Austausch des öffentlichen Schlüssels</li>' +
            '  <li>Verschlüsseln der Daten mithilfe des öffentlichen Schlüssels</li>' +
            '  <li>Verschlüsselte Daten können nur noch vom Empfänger mit privaten Schlüssel entschlüsselt werden</li>' +
            '</ol>'
        }
      ]
    }
  }
}
</script>
